<script setup lang="ts">
import { LucideSearch } from 'lucide-vue-next'
import type { IMultiselectOption } from '~/components/ui/multiselect'

const LOCALIZED_TITLE_CATEGORIES = ['credits', 'deposits', 'cards', 'vacancies', 'money_transfers', 'events']

const config = useRuntimeConfig()
const { t, locale } = useI18n()

interface ISearchResult {
  name: string
  url?: string
  title?: string
  title_global?: string
}

const mainSearchCategories = computed<IMultiselectOption[]>(() => [
  { id: 1, label: t('common.companies'), value: 'organizations', url: 'banks' },
  { id: 2, label: t('common.credits'), value: 'credits', url: 'credits' },
  { id: 3, label: t('common.deposits_label'), value: 'deposits', url: 'deposits' },
  { id: 4, label: t('common.cards'), value: 'cards', url: 'cards' },
  { id: 5, label: t('common.news'), value: 'news', url: 'news' },
  { id: 6, label: t('common.vacancies'), value: 'vacancies', url: 'vacancies' },
  {
    id: 7,
    label: t('common.money_transfers'),
    value: 'money_transfers',
    url: 'money-transfers'
  },
  { id: 8, label: t('common.discussions'), value: 'questions', url: 'faq' },
  { id: 9, label: t('common.events'), value: 'events', url: 'events' }
])

const selectedCategories = ref<IMultiselectOption[]>(
  computed(() => mainSearchCategories.value.slice(0, 5)).value
)
const searchQuery = ref('')
const activeCategories = computed(() =>
  selectedCategories.value.length > 0 ? selectedCategories.value : mainSearchCategories.value
)
const results = ref<Record<string, ISearchResult[]>>({})
const loadingByCategory = ref<Record<string, boolean>>({})
const searchInputRef = ref<HTMLInputElement | null>(null)

const searchClients = Object.fromEntries(
  ['organizations', 'credits', 'deposits', 'cards', 'news', 'vacancies', 'money_transfers', 'questions', 'events'].map((value) => {
    const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_${value}`)
    return [value, search]
  })
)

const emit = defineEmits<{
  (e: 'hide'): void
}>()

let searchTimeout: NodeJS.Timeout | null = null

// State to store search query by category
const getSearchState = (categoryUrl: string) => {
  return useState<string>(`searchQuery_${categoryUrl}`, () => '')
}
let abortController: AbortController | null = null
let isSearching = false

const searchCategory = async (cat: IMultiselectOption, query: string, signal?: AbortSignal) => {
  loadingByCategory.value = { ...loadingByCategory.value, [cat.value]: true }
  results.value = { ...results.value, [cat.value]: [] }

  try {
    const res = await searchClients[cat.value]?.(query, { hitsPerPage: 5 })
    if (signal?.aborted) return

    const hits = (res?.hits as ISearchResult[]) || []
    const processedHits = LOCALIZED_TITLE_CATEGORIES.includes(cat.value)
      ? hits.map((hit) => ({ ...hit, title: hit.title?.[locale.value] || hit.title }))
      : hits

    results.value = { ...results.value, [cat.value]: processedHits }
  } catch (e) {
    if (!signal?.aborted) {
      console.error(`${t('widget.news_categories.error_in_category')} ${cat.value}:`, e)
      results.value = { ...results.value, [cat.value]: [] }
    }
  } finally {
    loadingByCategory.value = { ...loadingByCategory.value, [cat.value]: false }
  }
}

const executeSearch = async (query: string, categories: IMultiselectOption[]) => {
  if (isSearching) return
  isSearching = true

  if (abortController) abortController.abort()
  abortController = new AbortController()
  const signal = abortController.signal

  const shouldRestoreFocus = import.meta.client && document.activeElement === searchInputRef.value

  try {
    for (const cat of categories) {
      await searchCategory(cat, query, signal)
      if (signal.aborted) return
    }

    if (shouldRestoreFocus) {
      await nextTick()
      searchInputRef.value?.inputRef?.focus()
    }
  } finally {
    isSearching = false
  }
}

const searchOnlyNewCategories = async (query: string, categories: IMultiselectOption[]) => {
  if (isSearching || categories.length === 0) return
  isSearching = true

  try {
    for (const c of categories) {
      await searchCategory(c, query)
    }
  } finally {
    isSearching = false
  }
}

const performSearch = async () => {
  const query = searchQuery.value.trim()
  if (query.length < 2) {
    results.value = {}
    loadingByCategory.value = {}
    return
  }

  await executeSearch(query, activeCategories.value)
}

const debouncedSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(performSearch, 500)
}

watch(
  searchQuery,
  (newQuery) => {
    const trimmed = newQuery.trim()
    if (trimmed.length < 2) {
      results.value = {}
      loadingByCategory.value = {}
      if (searchTimeout) clearTimeout(searchTimeout)
    } else {
      debouncedSearch()
    }
  },
  { flush: 'post' }
)

watch(
  selectedCategories,
  async (newCats, oldCats) => {
    const query = searchQuery.value.trim()
    if (query.length < 2 || isSearching) return

    const oldSet = new Set((oldCats || []).map((c) => c.value))
    const newSet = new Set(newCats.map((c) => c.value))

    const added = [...newSet].filter((v) => !oldSet.has(v))
    const removed = [...oldSet].filter((v) => !newSet.has(v))

    if (newCats.length === 0) {
      results.value = {}
      loadingByCategory.value = {}
      await executeSearch(query, mainSearchCategories.value)
      return
    }

    if (removed.length > 0) {
      results.value = Object.fromEntries(
        Object.entries(results.value).filter(([key]) => !removed.includes(key))
      )

      loadingByCategory.value = Object.fromEntries(
        Object.entries(loadingByCategory.value).filter(([key]) => !removed.includes(key))
      )
    }

    if (added.length > 0) {
      const addedCategories = newCats.filter((c) => added.includes(c.value))
      await searchOnlyNewCategories(query, addedCategories)
    }
  },
  { deep: true, flush: 'post' }
)

onMounted(() => {
  nextTick(() => {
    searchInputRef.value?.inputRef?.focus()
  })
})

onUnmounted(() => {
  if (searchTimeout) clearTimeout(searchTimeout)
  if (abortController) abortController.abort()
})

const onViewAllClick = (categoryUrl: string) => {
  if (searchQuery.value.trim()) {
    getSearchState(categoryUrl).value = searchQuery.value.trim()
  }
  emit('hide')
}
</script>

<template>
  <div class="w-full space-y-3 sm:space-y-5">
    <div class="flex flex-col gap-4 sm:flex-row">
      <div class="flex w-full items-stretch sm:h-12 sm:w-1/2">
        <MultiSelect
          v-model="selectedCategories"
          :options="mainSearchCategories"
          :placeholder="$t('menu.search.placeholder_categories')"
          class="multiselect_height_52"
        />
      </div>

      <div class="relative flex h-12 w-full items-stretch sm:w-1/2">
        <Input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          :placeholder="$t('menu.search.placeholder_query')"
          class="h-[52px] rounded border pl-12 pr-4"
        />
        <LucideSearch class="pointer-events-none absolute left-4 top-3.5 size-6 text-gray-600" />
      </div>
    </div>

    <div v-if="searchQuery.trim().length >= 2" class="max-h-[67vh] overflow-y-auto">
      <div>
        <div
          v-for="category in activeCategories"
          :key="category.value"
          class="mb-4"
        >
          <div class="mb-1 flex items-center gap-2">
            <Heading tag="h4">{{ category.label }}</Heading>
            <NuxtLinkLocale
              :to="`/${category.url}${searchQuery.length ? '?q=' + searchQuery : ''}`"
              class="text-blue-600 underline hover:no-underline"
              @click="onViewAllClick(category.url)"
            >
              {{ $t('menu.search.view_all') }}
            </NuxtLinkLocale>
          </div>

          <div v-if="loadingByCategory[category.value]" class="ml-6 text-gray-500">
            {{ $t('common.loading') }}
          </div>
          <div v-else-if="(results[category.value] || []).length === 0" class="ml-6 text-gray-500">
            {{ $t('menu.search.no_results') }}
          </div>

          <ul class="ml-6 space-y-1">
            <li v-for="(item, idx) in results[category.value]" :key="idx">
              <NuxtLinkLocale
                :to="item.url"
                class="text-blue-600 underline hover:no-underline"
                @click="emit('hide')"
              >
                <template v-if="category.value === 'organizations'"
                  >{{ item.type[locale] }}: {{ item.title[locale] }}</template
                >
                <template v-else>{{
                  item.title_global || item.title || item.name || $t('menu.search.untitled')
                }}</template>
              </NuxtLinkLocale>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
